import { AxisCode } from '@atlas/types'
import type { FilterAxis } from '@atlas/ui-components'
import type { ArticleListParams } from '@/domain/article.types'
import { useArticleFacetsForAxes } from '@/hooks/useArticles'
import { useAxesWithValues } from '@/hooks/useLibrary'

/**
 * The axis groups the article rail opens with. Every other pickable axis is one
 * click away under "Add variable", so this is a starting point rather than a limit.
 */
export const DEFAULT_ARTICLE_FILTER_AXES: string[] = [AxisCode.BRAND, AxisCode.GEOGRAPHY, AxisCode.SPECIES]

/**
 * The article rail's axes: every pickable axis is OFFERED, with labels and ordering
 * from the cached registry read, but a shown axis's VALUES come from the article
 * facet endpoint.
 *
 * The two sources are deliberate. The registry knows what axes exist and what they
 * are called; only the facets know which values any article actually carries. An
 * article corpus is small next to the taxonomy, so a brand axis offering all 40
 * registry values when three have articles would be a rail of dead ends.
 *
 * Counts ride along on each value, which is the density signal the index wants.
 */
export function useArticleFilterRailAxes(
	shownAxisCodes: string[],
	params: ArticleListParams,
): { axes: FilterAxis[]; isLoading: boolean } {
	const { axes: registryAxes, isLoading: registryLoading } = useAxesWithValues()
	const facetResults = useArticleFacetsForAxes(shownAxisCodes, params)

	const facetsByAxis = new Map(shownAxisCodes.map((axisCode, index) => [axisCode, facetResults[index]]))

	const axes: FilterAxis[] = registryAxes.map((axis) => {
		const facets = facetsByAxis.get(axis.code)
		if (!facets) {
			// Hidden: named but not counted, so revealing it is what triggers its read.
			return { code: axis.code, label: axis.label, options: [] }
		}
		return {
			code: axis.code,
			label: axis.label,
			options: (facets.data ?? []).map((value) => ({
				// The registry label, never the raw code — and the count, so a value
				// says how much is behind it.
				code: value.code,
				label: `${value.label} (${value.count})`,
				depth: 0,
			})),
			isLoading: facets.isFetching,
		}
	})

	return { axes, isLoading: registryLoading }
}
